import { camelizeKeys } from "humps";
import isEmpty from "lodash/isEmpty";
import { ReopenActivityModal } from "PFApp/components/reopen_activity_modal";
import { TemplateKey } from "PFApp/constants/templates";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { DropdownButton } from "PFComponents/dropdown_button";
import { isOpen } from "PFCore/helpers/activities";
import { isSubtemplate } from "PFCore/helpers/templates";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useActivityInvalidate, useActivityUpdate } from "PFCore/hooks/queries/activity";
import { Activity, FeatureFlag, PermissionRule } from "PFTypes";
import { CSSProperties, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useActivityPermissions } from "../../activities/hooks";
import { useTemplate } from "../../hooks";
import useIsRoleRemovalPermitted from "../../use_is_role_removal_permitted";
import { CloseConfirmModal } from "./modals/close_confirm_modal";
import { ExpiryDateWarningModal } from "./modals/expiry_date_warning_modal";
import { PublishConfirmModal } from "./modals/publish_confirm_modal";
import { RemoveConfirmModal } from "./modals/remove_confirm_modal";
import { ResetEngagementModal } from "./modals/reset_engagement_modal";

type ExpiryDateWarningMode = "publish" | "edit" | "clone";

type ExpiryDateWarningModalData = {
  show: boolean;
  mode: ExpiryDateWarningMode;
};

type MenuOption = {
  id: string;
  displayElement: React.ReactElement | string;
  item: () => void;
};

const ALLOWED_ACTIONS = [
  "publish",
  "edit",
  "clone",
  "close",
  "request_help",
  "apply_blueprint",
  "remove-co-owner",
  "remove",
  "share",
  "show_history"
] as const;

const AUDIT_ALLOWED_ACTIONS = ["reopen", "clone", "show_history", "close", "reset_roles"] as const;

type ActivityActionId = (typeof ALLOWED_ACTIONS)[number] | (typeof AUDIT_ALLOWED_ACTIONS)[number];

type ActivityActionsProps = {
  activity: Activity;
  handlePublish?: () => void;
  additionalOptions?: MenuOption[];
  dropDownStyle?: CSSProperties;
  handleEditPost?: (activityId: number) => void;
  qaId?: string;
  handleCloseActivitySuccess?: () => void;
  variant?: "button" | "icon";
  actionBlacklist?: ActivityActionId[];
};

const ActivityActions = ({
  activity,
  handlePublish,
  additionalOptions,
  dropDownStyle,
  handleEditPost,
  qaId,
  handleCloseActivitySuccess,
  variant,
  actionBlacklist
}: ActivityActionsProps) => {
  const { t } = useTranslation("core");

  const navigate = useNavigate();
  const { update: updateActivity } = useActivityUpdate(activity.id);
  const { invalidate: invalidateActivities } = useActivityInvalidate();
  const isPermittedTo = useIsCurrentUserPermittedTo();
  const isFeatureEnabled = useIsFeatureEnabled();

  const { showGrowl } = useGrowlStoreActions();
  const template = useTemplate(activity.template_id);
  const isSubtemplateMissing =
    !!activity.subtemplate_key && template && !isSubtemplate(template, activity.subtemplate_key);

  const isAudit = template?.key.includes("audit");
  const isAuditEngagement = template?.key === TemplateKey.AuditEngagement;

  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showExpiryDateModal, setShowExpiryDateModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [expiryDateWarningModalData, setExpiryDateWarningModalData] = useState<ExpiryDateWarningModalData>({
    show: false,
    mode: "publish"
  });

  const removePermitted = useIsRoleRemovalPermitted();
  const { canSeeHistory } = useActivityPermissions(activity);

  const publish = useCallback(() => {
    updateActivity({ state: "new" })
      .then(() => {
        showGrowl({
          kind: "success",
          title: t("activityActions.publishGrowlTitle"),
          message: t("activityActions.publishGrowl")
        });

        handlePublish?.();
      })
      .catch(() =>
        showGrowl({
          message: t("activityActions.publishFailGrowl"),
          kind: "error",
          url: `/activities/${activity.id}/edit`,
          onClick: () => navigate(`/activities/${activity.id}/edit`)
        })
      );
  }, [activity, updateActivity, showGrowl, t, navigate, handlePublish]);

  const handleExpiryDate = useCallback(
    (onNotExpiredCallback: () => void, mode: ExpiryDateWarningMode) => {
      const { post_until: postUntil } = activity;
      const isExpired = new Date(postUntil) < new Date();
      if (isExpired) {
        setExpiryDateWarningModalData({
          show: true,
          mode
        });
      } else {
        onNotExpiredCallback();
      }
    },
    [setExpiryDateWarningModalData, activity]
  );

  const commonOptions = useMemo(() => {
    const items: MenuOption[] = [];

    const activityEmpty =
      isEmpty(activity.metadata) && activity.custom_fields.every((field) => isEmpty(field.values));

    if (activity.coowned) {
      if (activity.state === "draft" && handlePublish) {
        items.push({
          id: "publish",
          displayElement: t("activityActions.publish"),
          item: () =>
            handleExpiryDate(() => (activityEmpty ? setShowPublishConfirm(true) : publish()), "publish")
        });
      }

      if (!isSubtemplateMissing) {
        items.push({
          id: "edit",
          displayElement: t("activityActions.edit"),
          item: () => {
            handleExpiryDate(
              () =>
                handleEditPost ? handleEditPost(activity.id) : navigate(`/activities/${activity.id}/edit`),
              "edit"
            );
          }
        });
      }

      if (isAudit && !isOpen(activity)) {
        items.push({
          id: "reopen",
          displayElement: t("activityActions.reopen"),
          item: () => setShowExpiryDateModal(true)
        });
      }

      if (isOpen(activity)) {
        // new or review
        items.push({
          id: "close",
          displayElement: t("activityActions.close"),
          item: () => setShowCloseConfirm(true)
        });
      }
    }

    if (
      template &&
      template.key !== "post" &&
      !template.hidden &&
      template.create_permitted &&
      !isSubtemplateMissing
    ) {
      items.push({
        id: "clone",
        displayElement: t("activityActions.clone"),
        item: () =>
          handleExpiryDate(
            () =>
              navigate(`/activities/${template.key}/new/${activity.id}`, {
                replace: true
              }),
            "clone"
          )
      });
    }

    if (canSeeHistory) {
      items.push({
        id: "show_history",
        displayElement: t("activityActions.showHistory"),
        item: () => navigate(`/activities/${activity.id}/history`)
      });
    }

    if (activity.coowned && removePermitted) {
      items.push({
        id: "remove",
        displayElement: t("activityActions.remove"),
        item: () => setShowRemoveConfirm(true)
      });
    }

    if (
      isAuditEngagement &&
      isPermittedTo(PermissionRule.AuditAdmin) &&
      isFeatureEnabled(FeatureFlag.AuditEngagementReset)
    ) {
      items.push({
        id: "reset_roles",
        displayElement: t("activityActions.reset"),
        item: () => setShowResetModal(true)
      });
    }

    return items;
  }, [
    activity,
    template,
    isSubtemplateMissing,
    canSeeHistory,
    removePermitted,
    handlePublish,
    isAudit,
    t,
    handleExpiryDate,
    publish,
    handleEditPost,
    navigate,
    isAuditEngagement,
    isPermittedTo,
    isFeatureEnabled
  ]);

  const options = useMemo(() => {
    const allOptions = additionalOptions ? commonOptions.concat(additionalOptions) : commonOptions;
    const items: MenuOption[] = [];

    (isAudit ? AUDIT_ALLOWED_ACTIONS : ALLOWED_ACTIONS).forEach((optionId) => {
      if (actionBlacklist?.includes(optionId)) {
        return;
      }
      const option = allOptions.find(({ id }) => id === optionId);
      if (option) {
        items.push(option);
      }
    });
    return items;
  }, [commonOptions, additionalOptions, isAudit, actionBlacklist]);

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <>
      <DropdownButton
        {...(variant === "button"
          ? {
              icon: "menu-vertical",
              buttonKind: "secondary"
            }
          : {
              actionIcon: "menu-horizontal"
            })}
        options={options}
        handleChange={(fn) => fn()}
        dropDownStyle={{ ...dropDownStyle, overflow: "visible" }}
        qaId={qaId}
        aria-label={t("activityActions.ariaLabel")}
      />
      {showExpiryDateModal && (
        <ReopenActivityModal
          activity={camelizeKeys(activity) as any}
          onClose={() => setShowExpiryDateModal(false)}
          onSuccess={() => invalidateActivities([activity.id])}
        />
      )}
      {showCloseConfirm && template && (
        <CloseConfirmModal
          activity={activity}
          handleClose={() => setShowCloseConfirm(false)}
          handleSuccess={() => handleCloseActivitySuccess?.()}
          type={template.key as TemplateKey}
        />
      )}
      {showRemoveConfirm && (
        <RemoveConfirmModal
          activity={activity}
          isAudit={!!isAudit}
          onClose={() => setShowRemoveConfirm(false)}
        />
      )}
      {showPublishConfirm && template && (
        <PublishConfirmModal
          templateKey={template.key as TemplateKey}
          onOk={publish}
          onClose={() => {
            setShowPublishConfirm(false);
          }}
        />
      )}
      {expiryDateWarningModalData.show && template && (
        <ExpiryDateWarningModal
          activityId={activity.id}
          templateKey={template.key}
          mode={expiryDateWarningModalData.mode}
          onClose={() => setExpiryDateWarningModalData({ show: false, mode: "publish" })}
        />
      )}
      {showResetModal && (
        <ResetEngagementModal
          activity={activity}
          onClose={() => setShowResetModal(false)}
          onSuccess={() => invalidateActivities([activity.id])}
        />
      )}
    </>
  );
};

export default ActivityActions;
